import type { RefObject } from 'react';
import { Button } from '@/design-system';
import type { Answers, AnswerValue, Catalogue, Section } from '@/lib/intake/catalogue';
import { applies, type Evaluation, isRequired } from '@/lib/intake/rules';
import { COPY } from './copy';
import { QuestionField } from './fields/QuestionField';
import styles from './Journey.module.css';

export interface SectionStepProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  catalogue: Catalogue;
  section: Section;
  number: number;
  answers: Answers;
  evaluation: Evaluation;
  touched: ReadonlySet<string>;
  editable: boolean;
  onAnswer: (field: string, value: AnswerValue | null) => void;
  onTouch: (field: string) => void;
  onBack: () => void;
  onNext: () => void;
}

/** One section's card: the questions that apply, drawn from the catalogue. */
export function SectionStep({ headingRef, ...props }: SectionStepProps) {
  const { catalogue, section, answers } = props;
  const total = catalogue.sections.length;
  return (
    <section className={styles.card} aria-labelledby="step-heading">
      <p className={styles.eyebrow}>{COPY.sectionEyebrow(props.number, total)}</p>
      <h1 id="step-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        {section.title}
      </h1>
      <p className={styles.intro}>{section.intro}</p>
      <div className={styles.questions}>
        {section.fields
          .filter((field) => applies(field, answers))
          .map((field) => (
            <QuestionField
              key={field.id}
              field={field}
              value={answers[field.id]}
              required={isRequired(field, answers)}
              issues={
                props.touched.has(field.id) ? props.evaluation.issues.filter((i) => i.field === field.id) : []
              }
              maxLength={field.type === 'textarea' ? catalogue.limits.textareaMaxLength : catalogue.limits.textMaxLength}
              disabled={!props.editable}
              onChange={(value) => props.onAnswer(field.id, value)}
              onBlur={() => props.onTouch(field.id)}
            />
          ))}
      </div>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={props.onBack}>
          {COPY.back}
        </Button>
        <Button onClick={props.onNext}>{props.number === total ? COPY.toDocuments : COPY.next}</Button>
      </div>
    </section>
  );
}
